import { useEffect, useRef } from "react";

import {
  buildAddressQuery,
  forwardGeocode,
  reverseGeocode,
  type AddressParts,
  type ReverseAddress,
} from "../api/geocoding";

/** A gemeente option as the intake/release feeds deliver it. */
export interface GemeenteOption {
  id: number;
  label: string;
  /** Absent on the release feed, which only carries id + label. */
  woonplaatsen?: string[];
}

function same(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Resolve the gemeente a reverse-geocoded address belongs to, the way Jmix does it
 * (StrayShelterIntakeFoundLocationFragment: findKnownMunicipality, then selectMunicipalityByCity):
 *
 * 1. the reported municipality matched against the gemeente names;
 * 2. otherwise the gemeente whose woonplaatsen contain the reported city (or municipality) — this is
 *    the case that matters, because Nominatim's `municipality` is regularly a name that is not a
 *    gemeente at all (a former municipality, a district), and the SPA then left the required Gemeente
 *    empty, which silently blocked Bevestigen after a map click;
 * 3. otherwise the city matched against the gemeente names.
 *
 * Returns null when nothing matches, so the caller keeps whatever the operator already chose.
 */
export function resolveGemeenteId(
  gemeenten: GemeenteOption[],
  address: { municipality: string | null; city: string | null },
): number | null {
  const { municipality, city } = address;
  const opNaam = (naam: string | null) => gemeenten.find((g) => same(g.label, naam));
  const opWoonplaats = (plaats: string | null) =>
    plaats ? gemeenten.find((g) => (g.woonplaatsen ?? []).some((w) => same(w, plaats))) : undefined;

  const hit =
    opNaam(municipality) ?? opWoonplaats(city) ?? opWoonplaats(municipality) ?? opNaam(city);
  return hit ? hit.id : null;
}

/**
 * Geocoding state-machine for the verblijf-wizard found-location maps, a faithful port of the behaviour
 * the Jmix fragments (StrayShelterIntakeFoundLocationFragment / NatureReleaseDetailsFragment) implemented
 * inline:
 *
 * - Forward: when the address fields change, geocode the assembled query and move the pin
 *   ({@link onResolvePin}). Debounced (Jmix fired synchronously on Vaadin's ON_CHANGE = blur/Enter; the SPA
 *   fields fire per keystroke, so we debounce to respect Nominatim's ≤1 req/s usage policy) and deduped on
 *   the query string (Jmix lastResolvedQuery).
 * - Reverse: a map click ({@link handlePick}) places the pin immediately, then reverse-geocodes and writes
 *   the address fields ({@link onReversePopulate}). Deduped on the rounded coordinate (Jmix
 *   lastReverseGeocodeKey).
 * - Suppression: the field writes a reverse-populate makes must NOT re-trigger a forward geocode that would
 *   bounce the just-placed pin — Jmix's suppressAutoGeocode flag. Here the next forward pass for the
 *   reverse-populated values is skipped (and its query recorded), which also encodes "a manually placed pin
 *   wins over re-geocoding its own address".
 *
 * The pin is primed on mount with the initial address query, so an edit-mode prefill (a saved pin) is never
 * bounced by an immediate forward-geocode of the already-saved address.
 */
export function useFoundLocationGeocoding(params: {
  address: AddressParts;
  /** Move the pin to a forward-geocoded coordinate (NOT marked as a manual placement). */
  onResolvePin: (lat: number, lng: number) => void;
  /** Populate the address fields from a reverse-geocoded map click. */
  onReversePopulate: (address: ReverseAddress) => void;
  /** When false, no network calls happen (e.g. the step isn't shown). */
  enabled?: boolean;
  /** Debounce for the forward geocode; overridable for tests. */
  debounceMs?: number;
}): { handlePick: (lat: number, lng: number) => void } {
  const { address, onResolvePin, onReversePopulate, enabled = true, debounceMs = 700 } = params;

  // Keep latest callbacks without re-running the forward effect.
  const onResolvePinRef = useRef(onResolvePin);
  onResolvePinRef.current = onResolvePin;
  const onReversePopulateRef = useRef(onReversePopulate);
  onReversePopulateRef.current = onReversePopulate;

  const query = buildAddressQuery(address);

  // Dedup + suppression state (Jmix: lastResolvedQuery / lastReverseGeocodeKey / suppressAutoGeocode).
  // lastQueryRef primed with the initial query → mount doesn't geocode an already-saved address.
  const lastQueryRef = useRef<string | null>(query);
  const lastReverseKeyRef = useRef<string | null>(null);
  const suppressRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    // A reverse-geocode just populated the fields → record the query and skip this one forward pass.
    if (suppressRef.current) {
      suppressRef.current = false;
      lastQueryRef.current = query;
      return;
    }
    if (!query || query === lastQueryRef.current) return;

    let cancelled = false;
    const handle = setTimeout(() => {
      lastQueryRef.current = query;
      void forwardGeocode(query).then((coord) => {
        if (!cancelled && coord) onResolvePinRef.current(coord.lat, coord.lon);
      });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, enabled, debounceMs]);

  const handlePick = (lat: number, lng: number): void => {
    onResolvePinRef.current(lat, lng); // the pin follows the click immediately
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (key === lastReverseKeyRef.current) return;
    lastReverseKeyRef.current = key;
    void reverseGeocode(lat, lng).then((addr) => {
      if (!addr) return;
      // The field writes below must not re-trigger a forward geocode (Jmix suppressAutoGeocode).
      suppressRef.current = true;
      onReversePopulateRef.current(addr);
    });
  };

  return { handlePick };
}
